import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Auth
import LoginPage from './pages/Login';
import ProtectedRoute from './routes/ProtectedRoute';
import { useInactivityLogout } from '@/hooks/useInactivityLogout';
import { useAuth } from '@/hooks/useAuth';
// A tab left open across a deploy keeps running the OLD bundle — that is what
// made the learning chart appear to ignore its origin filter on 2026-08-16.
import BuildStaleBanner from './components/BuildStaleBanner';

// ─── NEW COMMAND PAGES ──────────────────────────
import CommandDashboard from './pages/CommandDashboard';
// Every page but the dashboard and login is loaded on first visit (React.lazy): the single
// bundle was 1.1 MB, most of it chart, engine and lab code a given visit never opens.
const SimulationLab = lazy(() => import('./pages/SimulationLab'));
const ReportsJournal = lazy(() => import('./pages/ReportsJournal'));

// ─── PRO CHART ──────────────────────────────────
const ProChart = lazy(() => import('./pages/ProChart'));

// ─── UNIFIED SECTION PAGES ───────────────────────
const LearningLab = lazy(() => import('./pages/LearningLab/LearningLab'));
const MonitorPage = lazy(() => import('./pages/MonitorPage/MonitorPage'));
const ResearchLab = lazy(() => import('./pages/ResearchLab/ResearchLab'));
const JournalPage = lazy(() => import('./pages/Journal/JournalPage'));
const WeightsPage = lazy(() => import('./pages/Weights/WeightsPage'));
const SetupsPage = lazy(() => import('./pages/Setups/SetupsPage'));
const ContractsPage = lazy(() => import('./pages/Contracts/ContractsPage'));
const KnowledgePage = lazy(() => import('./pages/Knowledge/KnowledgePage'));

// ─── SHAPES MANAGEMENT ──────────────────────────

// ─── EXISTING MODULE PAGES (kept for deep-links) ──
const CoreBrain = lazy(() => import('./pages/Modules/CoreBrain'));
const LanguageIntelligence = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.LanguageIntelligence })));
const Memory = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.Memory })));
const ReasoningEngine = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.ReasoningEngine })));
const KnowledgeGraph = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.KnowledgeGraph })));
const MeaningEngine = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.MeaningEngine })));
const Perception = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.Perception })));
const ExplanationEngine = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.ExplanationEngine })));
const LearningEngine = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.LearningEngine })));
const Ontology = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.Ontology })));
const Simulation = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.Simulation })));
const ShapeEngine = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.ShapeEngine })));
const MarketIngestion = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.MarketIngestion })));
const CandleConstructor = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.CandleConstructor })));
const PriceObserver = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.PriceObserver })));
const EventBus = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.EventBus })));
const Gateway = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.Gateway })));
const SchemaRegistry = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.SchemaRegistry })));
const PolicyEngine = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.PolicyEngine })));
const SecurityCore = lazy(() => import('./pages/Modules/ModulePage').then(m => ({ default: m.SecurityCore })));
const LandingPage = lazy(() => import('./pages/Landing/LandingPage'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const SemanticDashboard = lazy(() => import('./pages/Dashboard/SemanticDashboard'));
const FutureScenarioViewer = lazy(() => import('./pages/DreamEngine/FutureScenarioViewer'));
const MeaningEnginePage = lazy(() => import('./pages/MeaningEngine'));
import CommandLayout from './layouts/CommandLayout';

function Shell({ children }: { children: ReactNode }) {
  return <CommandLayout>{children}</CommandLayout>;
}

/** Shown while a page's code is being fetched. */
function PageLoading() {
  return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: '#8b8b8b' }}>
      Loading…
    </div>
  );
}

function RootExperience() {
  // Auth bypass — always go straight to the dashboard
  return (
    <ProtectedRoute>
      <CommandDashboard />
    </ProtectedRoute>
  );
}

function App() {
  useInactivityLogout();
  useEffect(() => {
    console.log('[AUREXIS] Command System Initialized');
  }, []);

  return (
    <>
    <BuildStaleBanner />
    <Suspense fallback={<PageLoading />}>
    <Routes>
      {/* ══════════ PRIMARY COMMAND ROUTES ══════════ */}
      <Route path="/" element={<RootExperience />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><CommandDashboard /></ProtectedRoute>} />
      <Route path="/simulation" element={<ProtectedRoute><SimulationLab /></ProtectedRoute>} />
      <Route path="/features" element={<ProtectedRoute><ProChart surface="features" /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsJournal /></ProtectedRoute>} />
      {/* Its real 2FA/team-management/health logic was ported into Settings.tsx;
          the header's own links to it were the last reachable path, now fixed. */}
      <Route path="/monitoring" element={<Navigate to="/settings" replace />} />
      <Route path="/learning" element={<ProtectedRoute><LearningLab /></ProtectedRoute>} />
      {/* The learning controls as a page: the real autonomous-loop status, promotion ledger,
          freeze state and per-shape accept/reject verdicts (the old Start/Pause/Reward panel was fake). */}
      <Route path="/learning/control" element={<ProtectedRoute><Shell><LearningLab panelOnly /></Shell></ProtectedRoute>} />
      <Route path="/monitor" element={<ProtectedRoute><MonitorPage /></ProtectedRoute>} />
      <Route path="/research" element={<ProtectedRoute><ResearchLab /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
      {/* Every decision weight against its default and bounds, calibrator proposals (admin approve). */}
      <Route path="/setups" element={<ProtectedRoute><SetupsPage /></ProtectedRoute>} />
      <Route path="/weights" element={<ProtectedRoute><WeightsPage /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
      <Route path="/knowledge" element={<ProtectedRoute><KnowledgePage /></ProtectedRoute>} />
      {/* /brain duplicated /learning's review queue (chart-less) plus tabs that already
          have their own dedicated pages (Simulation, Perception Feed, Multi-Timeframe,
          Shapes) — consolidated into the one real, chart-integrated /learning surface. */}
      <Route path="/brain" element={<Navigate to="/learning" replace />} />

      {/* ══════════ AUTH ══════════ */}
      <Route path="/login" element={<LoginPage />} />

      {/* ══════════ EXISTING PAGES ══════════ */}
      <Route path="/candlestick-chart" element={<Navigate to="/chart" replace />} />
      <Route path="/chart" element={<ProtectedRoute><ProChart /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/semantic" element={<ProtectedRoute><Shell><SemanticDashboard /></Shell></ProtectedRoute>} />
      <Route path="/dream-scenarios" element={<ProtectedRoute><Shell><FutureScenarioViewer /></Shell></ProtectedRoute>} />
      {/* /performance duplicated ResearchLab's Growth panel exactly — both called
          learning-engine's /api/v2/ml/performance/growth with the same view filter,
          this one just in a separate, inconsistently-styled page nobody linked to
          on purpose. Retired; the real thing lives at /research. */}
      <Route path="/performance" element={<Navigate to="/research" replace />} />
      {/* /learning-console was a separate draw-to-teach workflow on an older chart engine
          (AurexisChart) doing, on one page, what the current app already splits across
          /chart (draw) and /learning (review) — consolidated, archived. */}
      <Route path="/learning-console" element={<Navigate to="/learning" replace />} />
      {/* /brain-growth was a thinner, Tailwind-styled duplicate of the Brain
          Monitor tab on /monitor — same /api/v2/brain/observatory endpoint,
          same BMI/grade/win-rate fields, in a visual language inconsistent
          with the rest of the app. Its own copy referenced "Learning Console",
          a page already retired. Retired; the real thing lives at /monitor. */}
      <Route path="/brain-growth" element={<Navigate to="/monitor" replace />} />
      <Route path="/meaning" element={<ProtectedRoute><MeaningEnginePage /></ProtectedRoute>} />

      {/* ══════════ MODULE DEEP-LINKS ══════════ */}
      <Route path="/module/core-brain" element={<ProtectedRoute><CoreBrain /></ProtectedRoute>} />
      <Route path="/module/language-intelligence" element={<ProtectedRoute><LanguageIntelligence /></ProtectedRoute>} />
      <Route path="/module/memory" element={<ProtectedRoute><Memory /></ProtectedRoute>} />
      <Route path="/module/reasoning-engine" element={<ProtectedRoute><ReasoningEngine /></ProtectedRoute>} />
      <Route path="/module/knowledge-graph" element={<ProtectedRoute><KnowledgeGraph /></ProtectedRoute>} />
      <Route path="/module/meaning-engine" element={<ProtectedRoute><MeaningEngine /></ProtectedRoute>} />
      <Route path="/module/perception" element={<ProtectedRoute><Perception /></ProtectedRoute>} />
      <Route path="/module/explanation-engine" element={<ProtectedRoute><ExplanationEngine /></ProtectedRoute>} />
      <Route path="/module/learning-engine" element={<ProtectedRoute><LearningEngine /></ProtectedRoute>} />
      <Route path="/module/ontology" element={<ProtectedRoute><Ontology /></ProtectedRoute>} />
      <Route path="/module/simulation" element={<ProtectedRoute><Simulation /></ProtectedRoute>} />
      <Route path="/module/shape-engine" element={<ProtectedRoute><ShapeEngine /></ProtectedRoute>} />
      <Route path="/module/market-ingestion" element={<ProtectedRoute><MarketIngestion /></ProtectedRoute>} />
      <Route path="/module/candle-constructor" element={<ProtectedRoute><CandleConstructor /></ProtectedRoute>} />
      <Route path="/module/price-observer" element={<ProtectedRoute><PriceObserver /></ProtectedRoute>} />
      <Route path="/module/event-bus" element={<ProtectedRoute><EventBus /></ProtectedRoute>} />
      <Route path="/module/gateway" element={<ProtectedRoute><Gateway /></ProtectedRoute>} />
      <Route path="/module/schema-registry" element={<ProtectedRoute><SchemaRegistry /></ProtectedRoute>} />
      <Route path="/module/policy-engine" element={<ProtectedRoute><PolicyEngine /></ProtectedRoute>} />
      <Route path="/module/security-core" element={<ProtectedRoute><SecurityCore /></ProtectedRoute>} />

      {/* Catch-all */}
      {/* The sixteen pages that sat behind the "More" menu (System Control, Mission Control, Training,
          Dream Engine, Knowledge Graph, Multi-Timeframe, Policy Approvals, Signals, Shapes, Market
          Intelligence, Market Terminal, Perception Feed, Engine Control, System Map, Admin Ops, API
          Consumers) were removed at the operator's request on 2026-10-12. Their source is kept under
          frontend/archive/removed-pages-2026-10/ - nothing was deleted. Old links fall through to home. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

export default App;
